import { useEffect, useSyncExternalStore } from 'react';
import { toResult, v1 } from '@/infrastructure/api/v1/client';

// The remote config settings that apply to the signed-in user (getRemoteConfig), by key; null until loaded
let settings: Record<string, unknown> | null = null;
let loading: Promise<void> | null = null;
// Bumped by each reload: a reply to an earlier read (the previous account's) is dropped
let generation = 0;
let failures = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

const RETRY_MS = [1_000, 3_000, 10_000, 30_000];

const notify = () => listeners.forEach(listener => listener());

/** Loads the settings once a page load: a setting changed in the database applies on the next reload. */
function load() {
  if (loading) return loading;
  const read = generation;
  loading = toResult(v1.GET('/api/v1/remote-config')).then(({ data, error }) => {
    if (read !== generation) return;
    if (error) {
      loading = null;
      failures += 1;
      if (failures <= RETRY_MS.length) {
        retryTimer = setTimeout(() => void load(), RETRY_MS[failures - 1]);
        return;
      }
      settings = {};
      notify();
      return;
    }
    failures = 0;
    settings = Object.fromEntries((data.settings ?? []).map(setting => [setting.key, setting.value]));
    notify();
  });
  return loading;
}

/** Drops the settings and reads them again, for a different signed-in account (the server filters them by its email). */
export function reloadRemoteConfig(): void {
  const wasLoaded = settings !== null || loading !== null || failures > 0;
  generation += 1;
  clearTimeout(retryTimer);
  failures = 0;
  settings = null;
  loading = null;
  notify();
  if (wasLoaded) void load();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && failures > RETRY_MS.length) reloadRemoteConfig();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * A remote config setting's value for the signed-in user; undefined while loading, when unset or when the request fails.
 * `read` false only follows the settings once something else has read them.
 */
export function useRemoteSetting(key: string, read = true): unknown {
  const value = useSyncExternalStore(subscribe, () => settings?.[key]);
  useEffect(() => {
    if (read) void load();
  }, [read]);
  return value;
}

const enabled = (value: unknown) => typeof value === 'object' && value !== null && (value as { enabled?: unknown }).enabled === true;

/** Whether a remote config flag is on: its value is {"enabled": true}. Off until loaded; `read` as useRemoteSetting. */
export function useRemoteFlag(key: string, read = true): boolean {
  return enabled(useRemoteSetting(key, read));
}

/** As useRemoteFlag, but undefined until the settings are read (or given up on), for a caller that waits for the answer. */
export function useRemoteFlagState(key: string, read = true): boolean | undefined {
  const value = useSyncExternalStore(subscribe, () => (settings === null ? undefined : enabled(settings[key])));
  useEffect(() => {
    if (read) void load();
  }, [read]);
  return value;
}

/** Whether a remote config flag is on, for code outside React: once the settings are read, and off when they can't be. */
export async function readRemoteFlag(key: string): Promise<boolean> {
  if (settings === null) await load();
  return enabled(settings?.[key]);
}
